import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React from 'react';

import { Section } from '@/components/molecules/Section';
import { useTranslator } from '@/hooks/useTranslator';
import { productCategoryOptions } from '@/Screens/Dashboard/Flows/TransferFlow/data';
import { SelectItem } from '@/types/fields';
import {
  ProductCategory,
  TradeDocumentDetails,
  TradeProduct,
  TradeType,
} from '@/types/transaction';

interface Props {
  readonly visible: boolean;
  readonly working: boolean;
  readonly tradeDocumentDetails: TradeDocumentDetails | null;
  readonly error: string | null;

  onChange(details: TradeDocumentDetails): void;
}

const emptyProduct: TradeProduct = {
  name: '',
  quantity: '',
  productCategory: ProductCategory.APPAREL,
};

const emptyDetails: TradeDocumentDetails = {
  tradeType: TradeType.GENERAL_GOODS,
  products: [emptyProduct],
  customs: { customsDecNo: '' },
  logistics: { logisticsCompany: '', logisticsOrderNo: '' },
};

export const TradeDocumentDetailsSection: React.FC<Props> = (props: Props): React.ReactElement => {
  const { visible, working, tradeDocumentDetails, error, onChange } = props;
  const tr = useTranslator();

  const details = tradeDocumentDetails ?? emptyDetails;

  const handleProductChange = React.useCallback(
    (index: number, field: keyof TradeProduct, value: string): void => {
      const updatedProducts = details.products.map(
        (product: TradeProduct, i: number): TradeProduct =>
          i === index ? { ...product, [field]: value } : product,
      );
      onChange({ ...details, products: updatedProducts });
    },
    [details, onChange],
  );

  const handleAddProduct = React.useCallback((): void => {
    onChange({ ...details, products: [...details.products, emptyProduct] });
  }, [details, onChange]);

  const handleRemoveProduct = React.useCallback(
    (index: number): void => {
      onChange({
        ...details,
        products: details.products.filter((_: TradeProduct, i: number): boolean => i !== index),
      });
    },
    [details, onChange],
  );

  const handleCustomsChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      onChange({ ...details, customs: { customsDecNo: event.target.value } });
    },
    [details, onChange],
  );

  const handleLogisticsChange = React.useCallback(
    (field: 'logisticsCompany' | 'logisticsOrderNo' | 'shipDate', value: string): void => {
      onChange({
        ...details,
        logistics: { ...details.logistics, [field]: value || undefined },
      });
    },
    [details, onChange],
  );

  // Initialize details on first render when visible
  React.useEffect((): void => {
    if (visible && tradeDocumentDetails === null) {
      onChange(emptyDetails);
    }
  }, [visible, tradeDocumentDetails, onChange]);

  return (
    <Section title={tr('TRADE_DOCUMENT_DETAILS_TITLE')} visible={visible} working={working}>
      <Stack gap={3}>
        <Box
          component="fieldset"
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            p: 2,
            m: 0,
          }}>
          <Typography component="legend" variant="body2" color="text.disabled" sx={{ px: 1 }}>
            {tr('TRADE_DOCUMENT_PRODUCTS_TITLE')}
          </Typography>
          <Stack gap={2}>
            {details.products.map(
              (product: TradeProduct, index: number): React.ReactElement => (
                <Box
                  key={index}
                  sx={index > 0 ? { borderTop: 1, borderColor: 'divider', pt: 2 } : undefined}>
                  <Stack gap={2}>
                    {index > 0 && (
                      <Box display="flex" justifyContent="flex-end">
                        <IconButton
                          size="small"
                          onClick={(): void => handleRemoveProduct(index)}
                          aria-label={tr('TRADE_DOCUMENT_REMOVE_PRODUCT')}>
                          <Typography variant="body2" color="error">
                            {tr('TRADE_DOCUMENT_REMOVE_PRODUCT')}
                          </Typography>
                        </IconButton>
                      </Box>
                    )}
                    <TextField
                      label={tr('TRADE_DOCUMENT_PRODUCT_NAME')}
                      value={product.name}
                      fullWidth
                      onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
                        handleProductChange(index, 'name', event.target.value)
                      }
                    />
                    <TextField
                      label={tr('TRADE_DOCUMENT_QUANTITY')}
                      value={product.quantity}
                      fullWidth
                      onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
                        handleProductChange(index, 'quantity', event.target.value)
                      }
                    />
                    <FormControl fullWidth>
                      <InputLabel id={`product-category-label-${index}`}>
                        {tr('TRADE_DOCUMENT_PRODUCT_CATEGORY')}
                      </InputLabel>
                      <Select
                        labelId={`product-category-label-${index}`}
                        value={product.productCategory}
                        label={tr('TRADE_DOCUMENT_PRODUCT_CATEGORY')}
                        onChange={(event: SelectChangeEvent<string>): void =>
                          handleProductChange(index, 'productCategory', event.target.value)
                        }>
                        {productCategoryOptions.map(
                          (option: SelectItem<ProductCategory>): React.ReactElement => (
                            <MenuItem key={option.value} value={option.value}>
                              {tr(option.label as string)}
                            </MenuItem>
                          ),
                        )}
                      </Select>
                    </FormControl>
                  </Stack>
                </Box>
              ),
            )}

            <Button variant="outlined" onClick={handleAddProduct}>
              {tr('TRADE_DOCUMENT_ADD_PRODUCT')}
            </Button>
          </Stack>
        </Box>

        <TextField
          label={tr('TRADE_DOCUMENT_CUSTOMS_DECLARATION_NO')}
          value={details.customs.customsDecNo}
          fullWidth
          onChange={handleCustomsChange}
        />

        <TextField
          label={tr('TRADE_DOCUMENT_LOGISTICS_COMPANY')}
          value={details.logistics.logisticsCompany}
          fullWidth
          onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
            handleLogisticsChange('logisticsCompany', event.target.value)
          }
        />
        <TextField
          label={tr('TRADE_DOCUMENT_LOGISTICS_ORDER_NO')}
          value={details.logistics.logisticsOrderNo}
          fullWidth
          onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
            handleLogisticsChange('logisticsOrderNo', event.target.value)
          }
        />
        <TextField
          label={tr('TRADE_DOCUMENT_SHIP_DATE')}
          type="date"
          value={details.logistics.shipDate ?? ''}
          fullWidth
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
            handleLogisticsChange('shipDate', event.target.value)
          }
        />

        {error && (
          <Box textAlign="center">
            <Typography color="error">{error}</Typography>
          </Box>
        )}
      </Stack>
    </Section>
  );
};
